import type { SkinRuntimeManifest } from '@kenocyber/shared';
import { useEffect, useState } from 'react';
import { fetchSkinManifest, findSkinEntry, skinAssetUrl } from '../game/skin-runtime';

/**
 * A skin's picture, for the screens React draws (gate V24C).
 *
 * ## Why this exists at all
 *
 * A skin's PNG is NOT in the main asset manifest. It is declared in the
 * pipeline's own sidecar, `game-assets/skin-manifest.json`, together with the
 * pivot, the hull centre, the visual bounds and the VFX descriptor - because a
 * skin is content with geometry, not an icon. Until now only the Phaser scene
 * and the benchmark read it, so every React screen that asked
 * `staticAssetUrl(manifest, definitionId)` got `null` and drew its "sem prévia"
 * hole. In production that answer happened to be right - no skin has art there -
 * so nothing ever noticed.
 *
 * The reveal cannot live with that: showing the skin somebody just bought is the
 * point of it.
 *
 * ## One request per page, shared by every caller
 *
 * The promise is a module-level cache, so a wardrobe with six cards and a reveal
 * over it make ONE request between them, and re-opening the panel makes none.
 * The browser caches the PNGs themselves; this caches the description of them.
 *
 * ## Production still shows nothing, and that is correct
 *
 * `listSelectableSkins` refuses to enumerate anything but `ready` unless dev
 * tooling is compiled in - a build constant Vite replaces, so the branch is
 * removed from a production bundle rather than being a check somebody could
 * flip. The three official skins are `draft`, so a production build resolves
 * them to `null` and the honest hole is drawn.
 */

/** Shared across every mount. Null until somebody asks for the first time. */
let pending: Promise<SkinRuntimeManifest | null> | null = null;

function loadOnce(): Promise<SkinRuntimeManifest | null> {
  pending ??= fetchSkinManifest().catch(() => null);
  return pending;
}

/**
 * Resolves a definition id to a PNG URL, or null.
 *
 * Null is a real answer and the caller draws its own hole for it: the sidecar
 * may be absent (a tree that was never prepared), the build may ship no skins,
 * or the definition may be draft content this build refuses to enumerate.
 */
export function useSkinArt(): (definitionId: string) => string | null {
  const [manifest, setManifest] = useState<SkinRuntimeManifest | null>(null);

  useEffect(() => {
    let alive = true;
    void loadOnce().then((loaded) => {
      if (alive) {
        setManifest(loaded);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  return (definitionId: string): string | null => {
    if (!manifest) {
      return null;
    }
    const entry = findSkinEntry(manifest, definitionId);
    return entry ? skinAssetUrl(manifest, entry) : null;
  };
}

/** Drops the shared cache. For tests, and for nothing the product does. */
export function resetSkinArtCache(): void {
  pending = null;
}
